import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import isNil from 'lodash/isNil';
import isNumber from 'lodash/isNumber';
import isString from 'lodash/isString';

import SectionTitle from 'core/modules/SectionTitle/SectionTitle';

import {
  selectErrorMessageConfig,
  selectErrorMessageCode,
  selectErrorMessageTitle,
  selectErrorMessageText,
  selectShouldRenderErrorCode,
} from './selectors';

import { saveErrorMessageFromPage } from './actions';

import './ErrorMessages.scss';

// If template value is a string then trim it and if it is empty then return 'undefined' in order to ignore such value
function ignoreEmptyString(value) {
  if (!isString(value)) {
    return value;
  }

  return value.trim().length > 0 ? value.trim() : undefined;
}

function getErrorDataFromPage() {
  let { code } = window.errorData || {};
  const { title, message } = window.errorData || {};

  // Sometimes smarty could pass a negative error code value to the element. The '-' prefix should be striped out
  if (isNumber(code) || isString(code)) {
    code = Math.abs(code);
  }

  return {
    // Validation: ignore empty strings in properties. Set such properties to undefined to be ignored in state
    code: ignoreEmptyString(code),
    title: ignoreEmptyString(title),
    message: ignoreEmptyString(message),
  };
}

function ErrorMessage() {
  const config = useSelector(selectErrorMessageConfig);
  const code = useSelector(selectErrorMessageCode);
  const title = useSelector(selectErrorMessageTitle);
  const message = useSelector(selectErrorMessageText);
  const shouldRenderErrorCode = useSelector(selectShouldRenderErrorCode);
  const { returnUrl } = config;
  const dispatch = useDispatch();

  useEffect(() => {
    const errorData = getErrorDataFromPage(); // Object is hardcoded in error page template
    if (!isNil(errorData.code)) {
      dispatch(saveErrorMessageFromPage(errorData));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!code && code !== 0) {
    return null;
  }

  return (
    <div className="mn_errorMessage" data-test="error-message">
      <SectionTitle title={title} useHtmlMarkup data-test="error-heading">
        {shouldRenderErrorCode && (
          <span className="mn_errorCode" data-test="error-code">{code}</span>
        )}
      </SectionTitle>
      <div className="mn_sectCont">
        <p
          className="mn_errorSubheading"
          data-test="error-subheading"
          dangerouslySetInnerHTML={{ __html: message }}
        />
        {returnUrl &&
          <p
            data-test="error-return-url"
            dangerouslySetInnerHTML={{ __html: returnUrl }}
          />
        }
      </div>
    </div>
  );
}

export default ErrorMessage;
